<template>
  <div ref="rootRef" class="matching-game" :class="{ fullscreen: isFullscreenRef }">
    <div v-if="featureEnabledRef === undefined || loadingRef" class="matching-status">
      <LoadingIndicator/>
    </div>

    <div v-else-if="errorRef" class="matching-status">
      <p>The game couldn't be loaded.</p>
      <button class="btn btn-primary" @click="loadDefinition">Try again</button>
    </div>

    <template v-else>
      <div class="game-bar">
        <div class="game-title">
          <h1>{{ gameTitleRef }}</h1>
          <div v-if="gameSubtitleRef" class="game-subtitle">{{ gameSubtitleRef }}</div>
        </div>
        <button
          v-if="setupDoneRef && !endGameRef"
          class="btn btn-link hint-btn"
          aria-label="Hint"
          :disabled="remainingPairCountRef < 4"
          @click="showHint"
        >
          <ZapIcon/>
        </button>
        <button
          v-if="setupDoneRef && !endGameRef"
          class="btn btn-link peek-btn"
          aria-label="Peek at all cards"
          :disabled="peekingRef || lockRef || phaseRef !== 'idle'"
          @click="peek"
        >
          <EyeIcon/>
        </button>
        <button
          v-if="setupDoneRef && !endGameRef"
          class="btn btn-link skip-btn"
          aria-label="End game"
          @click="skipToEnd"
        >
          <SkipForwardIcon/>
        </button>
        <button
          class="btn btn-link mute-btn"
          :aria-label="mutedRef ? 'Unmute' : 'Mute'"
          @click="sounds.toggleMute()"
        >
          <VolumeXIcon v-if="mutedRef"/>
          <Volume2Icon v-else/>
        </button>
        <button
          class="btn btn-link fullscreen-btn"
          :aria-label="isFullscreenRef ? 'Exit fullscreen' : 'Fullscreen'"
          @click="toggleFullscreen"
        >
          <MinimizeIcon v-if="isFullscreenRef"/>
          <MaximizeIcon v-else/>
        </button>
      </div>

      <MatchingGameSetup
        v-if="!setupDoneRef"
        :teams="teamsRef"
        :colors="teamColorsRef"
        :names="teamNamesRef"
        :first-pair="firstCardPairRef"
        :first-image-src="firstImageSrcRef"
        @update:color="setTeamColor"
        @update:name="setTeamName"
        @start="startGame"
      />

      <template v-else>
      <div ref="playAreaRef" class="play-area" :class="{ 'end-game': endGameRef }">
        <div
          v-for="team in teamsRef"
          :key="team.id"
          class="team-panel"
          :class="[team.side, endGameRef && winnerTeamRef !== null && winnerTeamRef !== team.id && 'loser']"
          :style="{ paddingTop: endGameRef ? undefined : `${alignTopRef}px` }"
        >
          <div class="team-circle team-indicator" :style="{ background: teamColorsRef[team.id] }"></div>
          <div class="team-label">Team</div>
          <div class="team-name">{{ teamNamesRef[team.id] }}</div>
          <div class="team-score-wrap">
            <div class="team-score" :class="{ bump: scoreBumpTeamsRef.includes(team.id) }">{{ team.matches.length }}</div>
            <MatchingConfetti :trigger="team.matches.length" :color="teamColorsRef[team.id]"/>
          </div>
          <div v-if="!endGameRef" class="team-stack" :ref="el => setStackRef(team.id, el)">
            <div
              v-for="(pair, i) in team.matches"
              :key="i"
              class="stack-item"
              :style="{ '--stack-i': i, visibility: reviewModeRef && revealPairRef?.id === pair.id ? 'hidden' : undefined }"
              @click="reviewMatch(pair, $event)"
            >
              <MatchingImageCard
                :src="imageSrc(pair.images[0])"
                :focal="pair.images[0]"
                :relationship="pair.relationship"
                :alt="pair.name"
              />
            </div>
          </div>
          <div v-else class="team-pairs">
            <div
              v-for="(pair, i) in team.matches"
              :key="i"
              class="pair-unit"
              :style="{ '--pair-size': `${gridCardSizeRef * 0.75}px` }"
              @click="reviewMatch(pair, $event)"
            >
              <div class="pair-card">
                <MatchingImageCard
                  :src="imageSrc(pair.images[0])"
                  :focal="pair.images[0]"
                  :relationship="pair.relationship"
                  :alt="pair.name"
                />
              </div>
              <div class="pair-name">{{ pair.name }}</div>
            </div>
          </div>
        </div>

        <div class="board-area">
          <div v-if="endGameRef" class="end-scoreboard">
            <div class="final-row">
              <div class="final-label">Final</div>
            </div>
            <button class="btn btn-primary btn-lg" @click="resetGame">Play again</button>
          </div>
          <div
            v-show="!endGameRef"
            ref="boardRef"
            class="board-grid"
            :style="{ gridTemplateColumns: `auto repeat(${columnCountRef}, 1fr)`, '--grid-gap': `${gridGapRef}px`, maxWidth: boardMaxWidthRef ? `${boardMaxWidthRef}px` : undefined }"
          >
            <div class="corner"></div>
            <div v-for="col in columnLabelsRef" :key="col" class="col-label">{{ col }}</div>
            <template v-if="dealReadyRef">
            <template v-for="(row, r) in rowsRef" :key="r">
              <div class="row-label">{{ r + 1 }}</div>
              <button
                v-for="(card, ci) in row"
                :key="card.key"
                :data-key="card.key"
                class="card"
                :class="{ 'is-up': isFaceUp(card), selected: isSelected(card), matched: isMatched(card), collected: collectedKeysRef.includes(card.key), hint: hintBlinkingRef && hintPairIdsRef.includes(card.pairId) }"
                :style="{ '--deal-i': r * columnCountRef + ci, gridColumnStart: cardColumnStart(row, ci), '--nudge-x': `${selectionNudgeRef[card.key]?.x ?? 0}px`, '--nudge-y': `${selectionNudgeRef[card.key]?.y ?? 0}px` }"
                type="button"
                @click="flip(card)"
              >
                <div class="card-inner">
                  <div class="card-back"></div>
                  <div class="card-front">
                    <div v-if="card.face === 'image'" class="card-image">
                      <img :src="imageSrc(card.pair.images[0])" v-focal="card.pair.images[0]" :alt="card.pair.name"/>
                    </div>
                    <div v-else class="card-info">
                      <div class="card-name">{{ card.pair.name }}</div>
                      <div class="card-info-middle">
                        <div class="card-text">{{ card.pair.text }}</div>
                      </div>
                    </div>
                    <div class="card-footer" :class="{ 'reveal-name': card.face === 'image' }">
                      <div v-if="card.face === 'image'" class="card-flip-name">{{ firstName(card.pair) }}</div>
                      <span
                        v-for="(step, i) in relationshipPath(card.pair)"
                        :key="i"
                        class="rel-square"
                        :class="step"
                      ></span>
                    </div>
                  </div>
                </div>
              </button>
            </template>
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="!endGameRef"
        class="turn-bar"
        :style="{
          top: `${turnBarTopRef}px`,
          opacity: turnBarVisibleRef ? 1 : 0,
          transition: turnBarTransitionRef,
          transform: `translateX(${turnBarXRef - (turnBarWidth * uiScaleRef) / 2}px)`,
        }"
      ></div>

      <!-- match reveal: click an image to focus it, click the backdrop to collect -->
      <div v-if="revealPairRef" class="reveal">
        <div class="reveal-scrim" :class="{ visible: revealVisibleRef }" @click="dismissReveal"></div>

        <div
          class="reveal-image"
          :class="{ dimmed: focusKeyRef && focusKeyRef !== 'primary', focused: focusKeyRef === 'primary' }"
          :style="{ width: `${imageBoxRef.w}px`, height: `${imageBoxRef.h}px`, transform: imageTransformRef }"
          @click.stop="focusImage('primary')"
        >
          <MatchingImageCard
            :src="imageSrc(revealImageRef)"
            :focal="revealFocalRef"
            :relationship="revealPairRef.relationship"
            :alt="revealPairRef.name"
          />
        </div>

        <div
          class="reveal-detail"
          :class="{ visible: revealVisibleRef, dimmed: focusKeyRef }"
          :style="{ width: `${detailBoxRef.w}px`, height: `${detailBoxRef.h}px`, transform: detailTransformRef }"
          @click.stop="dismissReveal()"
        >
          <MatchingDetailCard :pair="revealPairRef"/>
        </div>

        <div
          v-for="(sec, i) in revealSecondaryRef"
          :key="i"
          class="reveal-secondary"
          :class="{ visible: revealVisibleRef, dimmed: focusKeyRef && focusKeyRef !== ('sec-' + i) }"
          :style="{ width: `${sec.dw}px`, height: `${sec.dh}px`, transform: sec.transform }"
          @click.stop="focusImage('sec-' + i)"
        >
          <img :src="imageSrc(sec.image)" v-focal="focusKeyRef === ('sec-' + i) ? DEFAULT_FOCAL : sec.image" alt=""/>
        </div>
      </div>

      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.matching-game {
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;

  &.fullscreen {
    background: #fff;
  }
}

.matching-status {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.game-bar {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  // match the play area's 16:9 cap so the fullscreen button stays aligned with the board edge
  width: 100%;
  max-width: calc(100vh * 16 / 9);
  margin: 0 auto 1rem;
}

.game-title h1 {
  margin: 0;
  font-size: calc(3.75rem * var(--ui-scale, 1));
}

.game-subtitle {
  color: #888;
  font-size: calc(1.75rem * var(--ui-scale, 1));
}

.skip-btn,
.peek-btn,
.hint-btn,
.mute-btn,
.fullscreen-btn {
  position: absolute;
  top: 0;
  color: #aaa; // quiet controls, not calls-to-action

  &:hover {
    color: #888;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  svg {
    width: 28px;
    height: 28px;
  }
}

.skip-btn { right: 160px; }
.peek-btn { right: 120px; }
.hint-btn { right: 80px; }
.mute-btn  { right: 40px; }
.fullscreen-btn { right: 0; }

@keyframes hint-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.card.hint {
  z-index: 2; // float above adjacent cards so the ring isn't clipped

  // animate a ring on a pseudo-element so the card's own deal animation is unaffected
  &::after {
    content: '';
    position: absolute;
    inset: -6px;
    border: 4px solid #f7810a;
    border-radius: 18px; // card radius (12px) + inset (6px)
    animation: hint-blink 0.5s ease-in-out 3;
    pointer-events: none;
  }
}

.play-area {
  flex-grow: 1;
  display: flex;
  align-items: flex-start;
  // don't stretch across ultra-wide screens: cap the layout to 16:9 and centre it
  width: 100%;
  max-width: calc(100vh * 16 / 9);
  margin: 0 auto;
}

.team-panel {
  margin: 0 2rem;
  width: calc(96px * var(--ui-scale, 1));
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  &.left {
    order: 1;
  }

  &.right {
    order: 3;
  }
}

.turn-bar {
  position: fixed;
  left: 0;
  width: calc(72px * var(--ui-scale, 1));
  height: calc(10px * var(--ui-scale, 1));
  border-radius: calc(5px * var(--ui-scale, 1));
  background: #f7810a;
  z-index: 5;
}

.team-circle {
  width: calc(64px * var(--ui-scale, 1));
  height: calc(64px * var(--ui-scale, 1));
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.35);
  transition: width 0.5s ease, height 0.5s ease;
}

.team-label {
  font-size: calc(1.125rem * var(--ui-scale, 1)); // 0.75x the team name below it
  color: #888;
  line-height: 1;
  margin-bottom: -0.35rem; // sit just above the team name
}

.team-name {
  text-align: center;
  transition: font-size 0.5s ease;
  line-height: 1.15;
  font-size: calc(1.5rem * var(--ui-scale, 1));
}

.team-score-wrap {
  position: relative;
  margin: 1rem 0;
}

.team-score {
  font-size: calc(4rem * var(--ui-scale, 1));
  line-height: 1;
  font-weight: bold;

  &.bump {
    animation: score-pop 0.45s ease-out;
  }
}

// a quick punch-up that overshoots then settles — reads as "+1" without the goofy spin
@keyframes score-pop {
  0% {
    transform: scale(1);
  }

  45% {
    transform: scale(1.5);
  }

  70% {
    transform: scale(0.9);
  }

  100% {
    transform: scale(1);
  }
}

.team-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.stack-item {
  position: relative;
  // earlier cards paint on top, so each newer card peeks out below it — keeps every footer visible
  z-index: calc(var(--stack-i, 0) + 1);
  width: calc(84px * var(--ui-scale, 1));
  height: calc(84px * var(--ui-scale, 1));
  container-type: inline-size;
  cursor: pointer;
  --image-card-shadow: 1px 1px 4px rgba(0, 0, 0, 0.3);

  &:not(:first-child) {
    margin-top: calc(-25px * var(--ui-scale, 1)); // overlap the card above by ~30%
  }
}

.board-area {
  order: 2;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  transition: opacity 0.5s;
}

.board-grid {
  display: grid;
  gap: var(--grid-gap, 0.5rem);
  align-items: center;
  width: 100%;
}

.col-label,
.row-label {
  color: #000;
  font-size: 1.875rem;
  text-align: center;
}

.col-label {
  margin-bottom: calc(var(--grid-gap, 0.5rem) * -0.5);
}

.row-label {
  // padding (not margin) so the width the board-alignment JS measures includes this breathing room
  padding-right: calc(var(--grid-gap, 0.5rem) * 0.5);
}

.card {
  all: unset;
  position: relative;
  cursor: pointer;
  aspect-ratio: 1 / 1;
  perspective: 800px;
  // deal the cards onto the board: a staggered fade + pop, top-left to bottom-right
  animation: card-deal 0.4s ease backwards;
  animation-delay: calc(var(--deal-i, 0) * 0.028s);
  border-radius: 12px; // shapes the drop shadow to match the card faces
  // smoothly zoom in / slide over when picked, and back when deselected (see &.selected). Base has an
  // invisible shadow so .selected can fade a real one in; fade-out here is quick when deselecting.
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  transition: transform 0.3s ease, box-shadow 0.2s ease;

  .card-inner {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.4s;
    transform-style: preserve-3d;
  }

  &.is-up .card-inner {
    transform: rotateY(180deg);
  }

  // a picked card zooms up (so it's readable) and floats above the still-face-down cards. --nudge-x/-y
  // are set in JS for two orthogonally-adjacent picks, sliding them apart so they don't overlap on a
  // shared side; a lone pick (or diagonal picks) stays centred. scale kept in sync with the JS nudge.
  &.selected {
    z-index: 50;
    transform: translate(var(--nudge-x, 0), var(--nudge-y, 0)) scale(1.5);
    // the shadow grows in step with the zoom (0.5s, no delay) so the card reads as lifting off the
    // page. Zoom is a touch longer than the 0.4s flip, so the flip lands just before the lift settles
    // -- and the shadow is still small during the brief edge-on moment early in the flip.
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
    transition: transform 0.5s ease, box-shadow 0.5s ease;
  }

  &.matched {
    cursor: default;
  }

  &.collected {
    cursor: default;

    .card-inner {
      visibility: hidden;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border: 2px solid #eaeaea;
      border-radius: 12px;
    }
  }
}

@keyframes card-deal {
  from {
    opacity: 0;
    transform: scale(0.4) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.card-back,
.card-front {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 12px;
  overflow: hidden;
}

.card-back {
    background-image: url('@/assets/playing-card-square.png');
    background-size: cover;
    background-position: center;
    filter: contrast(0.25) brightness(1.2) sepia(0.7);
//   background: #4a6fa5;
//   background-image: repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.08) 0, rgba(255, 255, 255, 0.08) 8px, transparent 8px, transparent 16px);
}

.card-front {
  transform: rotateY(180deg);
  background: #fff;
  // shared with the setup preview: --card-border-width defaults to 1px and is bumped by an
  // ancestor for the enlarged "large" layout used when a card is flipped up.
  border: var(--card-border-width, 1px) solid #ccc;
  display: flex;
  flex-direction: column;
  // size the contents relative to the card box so text fits at any grid/fullscreen scale
  container-type: inline-size;
}

.matched .card-front {
  border-color: #4a6fa5;
}

.card-image {
  position: relative;
  flex-grow: 1;
  overflow: hidden;

  img {
    // sizing and placement are applied by v-focal
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.card-info {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.card-name {
  padding: 5cqi 5cqi 0;
  font-size: 10cqi;
  font-weight: bold;
  text-align: center;
  line-height: 1.15;
}

.card-info-middle {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5cqi;
}

.card-text {
  font-size: 10cqi;
  color: #666;
  text-align: center;
  line-height: 1.15;
}

.card-footer {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2.5cqi;
  padding: 4cqi;
}

// image card: the given name is flashed in the footer, centered on the squares, then crossfades to them
.card-flip-name {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4cqi;
  font-size: 10cqi;
  font-weight: bold;
  line-height: 1;
  text-align: center;
  opacity: 0; // hidden until the card flips up
  pointer-events: none;
}

// hold the name for ~2s after the flip, then a slow crossfade: name out, squares in
.card.is-up .card-footer.reveal-name {
  .card-flip-name {
    animation: flip-name-show 3.5s ease-in-out forwards;
  }

  .rel-square {
    animation: flip-squares-show 3.5s ease-in-out forwards;
  }
}

@keyframes flip-name-show {
  0%, 57% { opacity: 1; }
  100% { opacity: 0; }
}

@keyframes flip-squares-show {
  0%, 57% { opacity: 0; }
  100% { opacity: 1; }
}

.rel-square {
  width: 6cqi;
  height: 6cqi;
  border-radius: 1.5cqi;

  &.f {
    background: #73a7f0;
  }

  &.m {
    background: #f2c1d6;
  }
}

.reveal {
  position: fixed;
  inset: 0;
  z-index: 60;
  pointer-events: none;
}

.reveal-scrim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.4s;
  pointer-events: auto;
  cursor: pointer;

  &.visible {
    opacity: 1;
  }
}

.reveal-image,
.reveal-detail,
.reveal-secondary {
  position: fixed;
  top: 0;
  left: 0;
  transform-origin: top left;
}

.reveal-image {
  z-index: 3;
  transition: transform 0.6s ease, width 0.6s ease, height 0.6s ease, opacity 0.4s;
  pointer-events: auto;
  cursor: pointer;
  // the container is this fixed-size wrapper, so the footer scales with the card (not the viewport)
  container-type: inline-size;
  // the shared image card is drawn the same everywhere; only the drop shadow is bigger here (it floats)
  --image-card-shadow: 0 5px 22px rgba(0, 0, 0, 0.25);

  &.dimmed {
    opacity: 0;
    pointer-events: none;
  }

  // collapse/fade the footer while an image is focused, so the photo grows without a jump.
  // Don't transition padding: it's cqi, so on unfocus it would chase the shrinking card and land
  // late. max-height + overflow:hidden collapses the footer regardless, and its content height
  // (cqi padding + squares) already tracks the card each frame, so it settles right on time.
  :deep(.image-card-footer) {
    max-height: 20cqi;
    overflow: hidden;
    transition: opacity 0.35s ease, max-height 0.6s ease;
  }

  &.focused :deep(.image-card-footer) {
    opacity: 0;
    max-height: 0;
  }
}

.reveal-detail {
  z-index: 1;
  opacity: 0;
  transition: transform 0.6s ease, opacity 0.4s;
  // the container is the fixed-size wrapper, so the card's own padding/gap scale with the card (not the viewport)
  container-type: inline-size;
  // absorb taps so tapping the detail card doesn't fall through to the scrim and dismiss
  pointer-events: auto;

  &.visible {
    opacity: 1;
  }

  &.dimmed {
    opacity: 0;
    pointer-events: none; // while an image is focused, let taps through to the scrim to collect
  }
}

.reveal-secondary {
  z-index: 2;
  opacity: 0;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 12px;
  overflow: hidden;
  // strong shadow so the tightly-overlapped cards read as a stack, not a flat sheet
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  transition: opacity 0.4s, transform 0.5s ease, width 0.5s ease, height 0.5s ease;
  pointer-events: auto;
  cursor: pointer;

  &.visible {
    opacity: 1;
    // unfocusing back to the reveal: hold, then fade in near the end of the image's return
    transition: opacity 0.4s ease 0.55s, transform 0.5s ease, width 0.5s ease, height 0.5s ease;
  }

  &.dimmed {
    opacity: 0;
    pointer-events: none;
    // focusing to full view: fade out fast, right at the start (wins over .visible's delayed fade)
    transition: opacity 0.2s ease, transform 0.5s ease, width 0.5s ease, height 0.5s ease;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// same height as the team circles so FINAL is vertically centred with them
.final-row {
  display: flex;
  align-items: center;
  height: calc(112px * var(--ui-scale, 1));
}

.final-label {
  font-size: calc(2rem * var(--ui-scale, 1));
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.play-area.end-game {
  .board-area {
    flex: 0 0 auto;
  }

  // keep column flex — circle stays centred above the match cards.
  // score is pulled out of flow and floated beside the circle so it
  // doesn't shift the circle off-centre.
  .team-panel {
    position: relative;
    flex: 1 1 0;
    width: auto;
    align-items: center;
    gap: 0.25rem;
  }

  .team-circle {
    width: calc(112px * var(--ui-scale, 1));
    height: calc(112px * var(--ui-scale, 1));
  }

  // keep the end-game margin tweak, but let the font sizes match the play screen (base rules)
  .team-label {
    margin-top: 1rem;
    margin-bottom: 0;
  }

  .team-pairs {
    width: 100%;
    margin-top: 0.75rem;
  }

  // absolute so the score doesn't displace the circle in the column layout;
  // pinned to the circle's vertical centre (radius = 56px × scale)
  .team-score-wrap {
    display: block;
    position: absolute;
    top: calc(56px * var(--ui-scale, 1));
    transform: translateY(-50%);
    margin: 0;
  }

  // consistent gap: panel centre + circle radius + spacing
  .team-panel.left .team-score-wrap {
    left: calc(50% + 100px * var(--ui-scale, 1) + 1.5rem);
  }

  .team-panel.right .team-score-wrap {
    right: calc(50% + 100px * var(--ui-scale, 1) + 1.5rem);
  }

  .team-score {
    font-size: calc(5rem * var(--ui-scale, 1));
  }

  .team-panel.loser .team-score {
    color: #bbb;
  }
}

.end-scoreboard {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.team-pairs {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
}

.pair-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  animation: pair-in 0.4s ease both;
  cursor: pointer;
}

// sizing + query container for the shared MatchingImageCard (image + relationship footer)
.pair-card {
  width: var(--pair-size, 120px);
  height: var(--pair-size, 120px);
  container-type: inline-size;
  --image-card-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
}

.pair-name {
  width: var(--pair-size, 120px);
  font-size: 0.875rem;
  line-height: 1.15;
  text-align: center;
}

@keyframes pair-in {
  from {
    opacity: 0;
    transform: scale(0.85);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { EyeIcon, MaximizeIcon, MinimizeIcon, SkipForwardIcon, Volume2Icon, VolumeXIcon, ZapIcon } from '@zhuowenli/vue-feather-icons'
import { useFeatureStore, MatchingGameFeature } from '@/rd/FeatureStore'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import { MatchingCardPair, MatchingGameImage, MatchingGameDetail } from './MatchingGameDefinition'
import { useGamesApi, GameCardInfo, GameCardImage, GameCardDetail } from '../GamesApi'
import { vFocal, Focal } from '../vFocal'
import MatchingDetailCard from './MatchingDetailCard.vue'
import MatchingGameSetup from './MatchingGameSetup.vue'
import MatchingConfetti from './MatchingConfetti.vue'
import MatchingImageCard from './MatchingImageCard.vue'
import confetti from 'canvas-confetti'
import * as sounds from './GameSounds'
const { mutedRef } = sounds

// Shown when an image is "focused" to full view: default centred cover (no custom framing).
const DEFAULT_FOCAL: Focal = { focusX: 0.5, focusY: 0.5, zoom: 1 }

const props = defineProps<{ gameId?: string }>()

const DemoMemoryGameId = '25TWG3'
const flipBackDelayMs = 10000
const turnSwitchDelayMs = 500 // pause after the cards flip back before the turn passes
const turnBarSlideMs = 900 // turn bar transition time; board stays locked until it lands
const uiScaleReferenceWidth = 1440 // play-area width (px) at which the chrome is drawn at 1x
const dealWaitMs = 1800 // 39 cards × 28ms stagger + 400ms animation + ~300ms pause before spin
const detailHeightFraction = 0.85 // detail card height as a share of the viewport (consistent across window/fullscreen)
const maxAspect = 16 / 9 // cap the play area + reveal width to 16:9 on ultra-wide screens
const detailWidthFactor = 1.15
const detailHeightFactor = 1.6 // detail card height relative to the primary image (i.e. imageSize)
const secondaryFactor = 0.4
const revealOverlapFactor = 0.03 // image/detail/secondary cards overlap slightly
const detailCentreBias = 0.05 // detail card centred slightly right of page centre
const stackItemSize = 84
const turnBarWidth = 72
const floatAnimMs = 600
const circleInset = 4 // swatch offset above the team circle

type CardFace = 'image' | 'info'
type Phase = 'idle' | 'celebrate' | 'collecting'

interface PlayCard {
  key: number
  pairId: string
  face: CardFace
  pair: MatchingCardPair
}

interface SecondaryImage {
  image: MatchingGameImage
  w: number // resting square side
  dw: number // displayed width (grows to the image's natural ratio when focused)
  dh: number
  restTransform: string
  transform: string
}

const router = useRouter()
const featureStore = useFeatureStore()
const gamesApi = useGamesApi()

const rootRef = ref<HTMLElement>()
const boardRef = ref<HTMLElement>()
const playAreaRef = ref<HTMLElement>()
const isFullscreenRef = ref(false)
const alignTopRef = ref(0)
const teamCentersRef = ref<number[]>([])
const turnBarTopRef = ref(0)
const gridGapRef = ref(8)
const gridCardSizeRef = ref(0)
const boardMaxWidthRef = ref<number>()
const uiScaleRef = ref(1) // chrome scale factor, driven off the viewport so it grows in fullscreen
const endGameRef = ref(false)
let mismatchTimer = 0
let peekTimer = 0

const loadingRef = ref(false)
const errorRef = ref(false)

const cardPairsRef = ref<MatchingCardPair[]>([])
const deckRef = ref<PlayCard[]>([])
const flippedKeysRef = ref<number[]>([])
const peekingRef = ref(false)
const matchedPairIdsRef = ref(new Set<string>())
const collectedKeysRef = ref<number[]>([])
const lockRef = ref(false)

// team play
const currentTeamRef = ref(0)
const teamMatchesRef = ref<MatchingCardPair[][]>([[], []])

// turn bar: normally follows the current team; the "who goes first" spin overrides it
const turnBarOverrideRef = ref<number | null>(null)
const turnBarVisibleRef = ref(false)
const turnBarTransitionRef = ref('transform 0.9s ease-in-out')
const turnBarXRef = computed(() => turnBarOverrideRef.value ?? teamCentersRef.value[currentTeamRef.value] ?? 0)

// teams whose score is mid-celebration (drives the score bump animation)
const scoreBumpTeamsRef = ref<number[]>([])

// team colour + name picker
const teamColorsRef = ref(['#000000', '#ffffff'])
const teamNamesRef = ref(['Charcoal', 'Cotton ball'])
// The setup preview highlights the nearest paternal card: the shortest relationship path
// ending in "F" (father). Falls back to the first pair when none end in "F".
const firstCardPairRef = computed(() => {
  const pairs = cardPairsRef.value as MatchingCardPair[]
  const nearestFather = pairs
    .filter(p => (p.relationship || '').endsWith('F'))
    .reduce<MatchingCardPair | undefined>(
      (best, p) => (!best || p.relationship.length < best.relationship.length ? p : best),
      undefined)
  return nearestFather ?? pairs[0]
})
const firstImageSrcRef = computed(() => imageSrc(firstCardPairRef.value?.images?.[0]))
const setupDoneRef = ref(false)
const dealReadyRef = ref(false)
const gameTitleRef = ref('')
const gameSubtitleRef = ref('')

// match reveal
const phaseRef = ref<Phase>('idle')
const revealPairRef = ref<MatchingCardPair>()
const revealImageRef = ref<MatchingGameImage>()
// The primary reveal image's live focal. Focusing to full view keeps the stored focus point and
// only eases zoom -> 1 (over the same duration as the box grow), so the framing doesn't jump.
const revealFocalRef = ref<Focal>(DEFAULT_FOCAL)
let revealZoomRaf = 0
const revealSecondaryRef = ref<SecondaryImage[]>([])
const imageBoxRef = ref({ w: 0, h: 0 })
const detailBoxRef = ref({ w: 0, h: 0 })
const imageTransformRef = ref('')
const detailTransformRef = ref('')
const revealVisibleRef = ref(false)
const revealImageSizeRef = ref(0)
const imageRestRef = ref('')
const focusKeyRef = ref<string | null>(null)
const celebratingKeysRef = ref<number[]>([])
const stackEls: Record<number, HTMLElement | null> = {}

// re-opening a collected pair from a team stack (read-only review, no scoring)
const reviewModeRef = ref(false)
let reviewOriginEl: HTMLElement | null = null

// TODO restore feature gating once the RD 'MemoryGame' feature is declared server-side
const bypassFeatureGate = true
const featureEnabledRef = computed(() =>
  bypassFeatureGate ? true : featureStore.isFeatureEnabled(MatchingGameFeature))
const gameId = computed(() => props.gameId || DemoMemoryGameId)

// Board shape from the card count: the factor pair closest to square that stays landscape
// (cols > rows) and fills every cell -- rows = largest divisor with n / rows > rows -- but never
// more than 4 rows. Past that the board spreads into wider rows and the last row may fall short,
// centered by cardColumnStart. E.g. 20 -> 5x4, 24 -> 6x4, 28 -> 7x4, 30 -> 8x4 (last row of 6).
const maxBoardRows = 4
const columnCountRef = computed(() => {
  const n = deckRef.value.length
  if (n === 0) {
    return 1
  }
  let rows = Math.floor(Math.sqrt(n))
  while (rows > 1 && (n % rows !== 0 || n / rows <= rows)) {
    rows--
  }
  rows = Math.min(rows, maxBoardRows)
  return Math.ceil(n / rows)
})
// A short final row is centered by shifting its cards right past the empty columns.
// Grid column 1 is the row-label, so card columns start at 2.
function cardColumnStart(row: PlayCard[], cardIndex: number) {
  const offset = Math.floor((columnCountRef.value - row.length) / 2)
  return 2 + offset + cardIndex
}
const columnLabelsRef = computed(() =>
  Array.from({ length: columnCountRef.value }, (_, i) => String.fromCharCode(65 + i)))
const rowsRef = computed(() => {
  const cols = columnCountRef.value
  const rows: PlayCard[][] = []
  for (let i = 0; i < deckRef.value.length; i += cols) {
    rows.push(deckRef.value.slice(i, i + cols))
  }
  return rows
})

// grid coordinates (row index + CSS grid column) per card key, so we can tell when two picks are
// orthogonally adjacent. Grid column reflects the centring offset of a short final row, so cards
// that don't share a column aren't treated as vertically adjacent.
const cardGridPosRef = computed(() => {
  const map: Record<number, { r: number; col: number }> = {}
  rowsRef.value.forEach((row, r) => {
    row.forEach((card, ci) => { map[card.key] = { r, col: cardColumnStart(row, ci) } })
  })
  return map
})

// per-card slide (px) that pushes two orthogonally-adjacent picks apart so their zoomed boxes don't
// overlap on a shared side. A lone pick, or diagonally-adjacent picks (corner overlap is fine), get
// no nudge. Consumed as --nudge-x/--nudge-y by the .card.selected transform.
const selectionNudgeRef = ref<Record<number, { x: number; y: number }>>({})

watch([flippedKeysRef, gridCardSizeRef], ([keys]) => {
  const nudge: Record<number, { x: number; y: number }> = {}
  if (keys.length === 2) {
    const pos = cardGridPosRef.value
    const a = pos[keys[0]]
    const b = pos[keys[1]]
    // at scale 1.5, adjacent cards (~1.09 card-widths between centres) overlap by ~0.41 of a card.
    // 0.25 card-width per side clears the overlap down to exactly the normal grid gap; the extra
    // 0.25 grid-gap per side widens that to ~1.5x the normal gap. Keep in sync with scale(1.5).
    const shift = 0.25 * gridCardSizeRef.value + 0.25 * gridGapRef.value
    if (a && b && a.r === b.r && Math.abs(a.col - b.col) === 1) {
      const leftFirst = a.col < b.col
      nudge[leftFirst ? keys[0] : keys[1]] = { x: -shift, y: 0 }
      nudge[leftFirst ? keys[1] : keys[0]] = { x: shift, y: 0 }
    } else if (a && b && a.col === b.col && Math.abs(a.r - b.r) === 1) {
      const topFirst = a.r < b.r
      nudge[topFirst ? keys[0] : keys[1]] = { x: 0, y: -shift }
      nudge[topFirst ? keys[1] : keys[0]] = { x: 0, y: shift }
    }
  }
  selectionNudgeRef.value = nudge
})

const teamsRef = computed(() => [
  { id: 0, side: 'left', token: 'black', matches: teamMatchesRef.value[0] },
  { id: 1, side: 'right', token: 'white', matches: teamMatchesRef.value[1] },
])

const collectedCountRef = computed(() =>
  teamMatchesRef.value[0].length + teamMatchesRef.value[1].length)
const wonRef = computed(() =>
  cardPairsRef.value.length > 0 && collectedCountRef.value === cardPairsRef.value.length)
// the winning team id, or null on a tie
const winnerTeamRef = computed(() => {
  const a = teamMatchesRef.value[0].length
  const b = teamMatchesRef.value[1].length
  if (a === b) {
    return null
  }
  return a > b ? 0 : 1
})

// hint: blink an orange outline on 2 randomly chosen unmatched pairs
const hintPairIdsRef = ref<string[]>([])
const hintBlinkingRef = ref(false)
let hintTimer: number | undefined
const remainingPairCountRef = computed(() => cardPairsRef.value.length - matchedPairIdsRef.value.size)

function showHint() {
  hintPairIdsRef.value = hintPairIdsRef.value.filter(id => !matchedPairIdsRef.value.has(id))
  const need = 2 - hintPairIdsRef.value.length
  if (need > 0) {
    const existing = new Set(hintPairIdsRef.value)
    const remaining = cardPairsRef.value.filter(p => !matchedPairIdsRef.value.has(p.id) && !existing.has(p.id))
    const shuffled = [...remaining].sort(() => Math.random() - 0.5)
    hintPairIdsRef.value = [...hintPairIdsRef.value, ...shuffled.slice(0, need).map(p => p.id)]
  }
  hintBlinkingRef.value = false
  window.clearTimeout(hintTimer)
  requestAnimationFrame(() => {
    hintBlinkingRef.value = true
    hintTimer = window.setTimeout(() => { hintBlinkingRef.value = false }, 1600)
  })
}

// reset hint pairs when the turn switches so the next player gets a fresh selection
watch(currentTeamRef, () => { hintPairIdsRef.value = [] })

// celebrate the winner with confetti from their side once the final view has settled
watch(endGameRef, (ended) => {
  const winner = winnerTeamRef.value
  if (!ended || winner === null) {
    return
  }
  sounds.gameOver()
  window.setTimeout(() => {
    const circle = document.querySelectorAll<HTMLElement>('.team-circle')[winner]
    if (!circle) {
      return
    }
    const r = circle.getBoundingClientRect()
    const origin = { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight }
    const colors = [teamColorsRef.value[winner], '#f7810a', '#ffd400', '#39b54a', '#2e9bd6', '#e23b6d']
    confetti({ particleCount: 120, spread: 90, startVelocity: 45, origin, colors })
    confetti({ particleCount: 80, spread: 120, startVelocity: 30, origin, colors, scalar: 0.9 })
  }, 600) // let the team panels finish expanding first
})

// gate on the feature flag: unknown while loading, redirect away when disabled, load when enabled
watch(featureEnabledRef, (enabled) => {
  if (enabled === false) {
    router.replace('/games')
  } else if (enabled === true && cardPairsRef.value.length === 0 && !loadingRef.value) {
    loadDefinition()
  }
}, { immediate: true })

watch(() => deckRef.value.length, () => requestAnimationFrame(measureAlignment))

async function loadDefinition() {
  loadingRef.value = true
  errorRef.value = false
  try {
    // Data comes through the games API (like the designer); listCardInfo returns every card in one
    // call. Images are served straight from the public blob container -- see imageSrc.
    const [definition, cards] = await Promise.all([
      gamesApi.getDefinition(gameId.value),
      gamesApi.listCardInfo(gameId.value),
    ])
    if (!definition || cards.length === 0) {
      throw new Error('Game not found.')
    }
    cardPairsRef.value = cards.map(toCardPair)
    gameTitleRef.value = definition.title ?? 'Memory Game'
    gameSubtitleRef.value = definition.subtitle ?? ''
    setupDoneRef.value = false
    resetGame()
    // warm the browser cache now, during setup, while the player is choosing teams -- so board cards
    // and (especially) the match-reveal secondary images are already decoded when they're shown
    preloadImages()
  } catch {
    errorRef.value = true
  } finally {
    loadingRef.value = false
  }
}

// Map a normalized GameCardInfo (new data format) to the player's internal card-pair model, so the
// rest of the game -- deck, reveal, detail/image cards -- is unchanged.
function toCardPair(card: GameCardInfo): MatchingCardPair {
  return {
    id: card.id,
    name: card.name ?? '',
    text: card.text ?? '',
    relationship: card.relationship ?? '',
    flag: card.flag,
    notes: card.notes,
    childOrder: card.childOrder,
    siblingsCount: card.siblingsCount,
    childCount: card.childCount,
    images: (card.images ?? []).map(toImage),
    details: card.details?.map(toDetail),
  }
}

// Healed images live at images/{imageId}.jpg; a ported image not yet healed keeps its legacy path.
// Either way imageSrc resolves it against the game folder, so downstream code needs no change.
function toImage(image: GameCardImage): MatchingGameImage {
  return {
    path: image.path ?? `images/${image.imageId}.jpg`,
    focusX: image.focusX ?? 0.5,
    focusY: image.focusY ?? 0.5,
    zoom: image.zoom ?? 1,
  }
}

// The new model has no per-detail spouse (it's folded into value); the detail card handles its
// absence. Per-detail flag is carried through.
function toDetail(detail: GameCardDetail): MatchingGameDetail {
  return {
    date: detail.date ?? '',
    type: detail.type,
    value: detail.value,
    place: detail.place,
    flag: detail.flag,
    highlight: detail.highlight,
  }
}

function resetGame() {
  window.removeEventListener('click', onMismatchClick, true)
  window.clearTimeout(mismatchTimer)
  window.clearTimeout(hintTimer)
  window.clearTimeout(peekTimer)
  hintPairIdsRef.value = []
  hintBlinkingRef.value = false
  peekingRef.value = false
  endGameRef.value = false
  dealReadyRef.value = setupDoneRef.value // Play Again: cards appear immediately; first game: startGame controls timing
  const cards: PlayCard[] = []
  let key = 0
  for (const pair of cardPairsRef.value) {
    cards.push({ key: key++, pairId: pair.id, face: 'image', pair })
    cards.push({ key: key++, pairId: pair.id, face: 'info', pair })
  }
  deckRef.value = shuffle(cards)
  flippedKeysRef.value = []
  matchedPairIdsRef.value = new Set<string>()
  collectedKeysRef.value = []
  currentTeamRef.value = 0
  teamMatchesRef.value = [[], []]
  phaseRef.value = 'idle'
  clearReveal()
  lockRef.value = false
  // reset the turn bar; if we're restarting mid-play (Play again), re-run the who-goes-first spin
  turnBarOverrideRef.value = null
  turnBarTransitionRef.value = 'transform 0.9s ease-in-out'
  turnBarVisibleRef.value = false
  if (setupDoneRef.value) {
    sounds.placeCards() // startGame cues this on the first deal -- Play again re-deals here
    requestAnimationFrame(() => requestAnimationFrame(() => {
      measureAlignment()
      spinTurnBar()
    }))
  }
}

function shuffle<T>(items: T[]) {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = items[i]
    items[i] = items[j]
    items[j] = temp
  }
  return items
}

function isMatched(card: PlayCard) {
  return matchedPairIdsRef.value.has(card.pairId)
}

function isFaceUp(card: PlayCard) {
  return peekingRef.value || isMatched(card) || flippedKeysRef.value.includes(card.key)
}

// a card the player has picked this turn: face-up by choice and not yet collected into a match.
// These zoom up (and slide apart when adjacent) via the .selected class + --nudge-x/-y.
function isSelected(card: PlayCard) {
  return flippedKeysRef.value.includes(card.key) && !collectedKeysRef.value.includes(card.key)
}

// peek: flip every card face-up for a moment, then flip them all back
function peek() {
  if (peekingRef.value || lockRef.value || phaseRef.value !== 'idle') {
    return
  }
  peekingRef.value = true
  sounds.cardReveal()
  peekTimer = window.setTimeout(() => {
    sounds.cardHide()
    peekingRef.value = false
  }, 2000)
}

// give every remaining pair to the current team, then jump to the end screen
function skipToEnd() {
  if (endGameRef.value) {
    return
  }
  window.removeEventListener('click', onMismatchClick, true)
  window.clearTimeout(mismatchTimer)
  window.clearTimeout(hintTimer)
  window.clearTimeout(peekTimer)
  hintPairIdsRef.value = []
  hintBlinkingRef.value = false
  peekingRef.value = false
  flippedKeysRef.value = []
  lockRef.value = false
  const teamId = currentTeamRef.value
  const unclaimed = cardPairsRef.value.filter(pair => !matchedPairIdsRef.value.has(pair.id))
  matchedPairIdsRef.value = new Set(cardPairsRef.value.map(pair => pair.id))
  teamMatchesRef.value = teamMatchesRef.value.map((matches, id) =>
    id === teamId ? [...matches, ...unclaimed] : matches)
  endGameRef.value = true
}

function flip(card: PlayCard) {
  if (phaseRef.value !== 'idle' || lockRef.value) {
    return
  }
  if (isFaceUp(card) || flippedKeysRef.value.length === 2) {
    return
  }
  sounds.cardReveal()
  flippedKeysRef.value = [...flippedKeysRef.value, card.key]
  if (flippedKeysRef.value.length === 2) {
    resolveFlipped()
  }
}

function resolveFlipped() {
  const [firstKey, secondKey] = flippedKeysRef.value
  const first = deckRef.value.find(c => c.key === firstKey)
  const second = deckRef.value.find(c => c.key === secondKey)
  if (first && second && first.pairId === second.pairId) {
    sounds.matchSucceed()
    matchedPairIdsRef.value = new Set(matchedPairIdsRef.value).add(first.pairId)
    const imageCard = first.face === 'image' ? first : second
    const infoCard = first.face === 'info' ? first : second
    startCelebrate(imageCard, infoCard)
  } else {
    lockRef.value = true
    //window.setTimeout(() => sounds.matchFail(), 400) // after the 0.4s flip transition completes
    mismatchTimer = window.setTimeout(resolveMismatch, flipBackDelayMs)
    // a click flips the mismatched cards back right away
    requestAnimationFrame(() => window.addEventListener('click', onMismatchClick, true))
  }
}

function onMismatchClick(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  window.clearTimeout(mismatchTimer)
  resolveMismatch()
}

function resolveMismatch() {
  window.removeEventListener('click', onMismatchClick, true)
  const [firstKey] = flippedKeysRef.value
  // flip cards back one at a time with a sound each
  sounds.cardHide()
  flippedKeysRef.value = flippedKeysRef.value.filter(k => k !== firstKey)
  setTimeout(() => {
    sounds.cardHide()
    flippedKeysRef.value = []
    // keep the board locked through the hand-off: then the turn bar slides across
    setTimeout(() => {
      const nextTeam = 1 - currentTeamRef.value
      currentTeamRef.value = nextTeam
      setTimeout(() => {
        sounds.turnLanded(nextTeam)
        lockRef.value = false
      }, turnBarSlideMs)
    }, turnSwitchDelayMs)
  }, 200)
}

function cardEl(key: number) {
  return boardRef.value?.querySelector<HTMLElement>(`[data-key="${key}"]`) ?? null
}

// lay out the reveal composition (image + detail + secondaries) at their resting positions
function composeReveal(pair: MatchingCardPair) {
  // anchor the whole reveal on viewport height so the detail card is a consistent share of the page
  let imageSize = (window.innerHeight * detailHeightFraction) / detailHeightFactor
  let detailW = imageSize * detailWidthFactor
  let secW = imageSize * secondaryFactor
  // keep the image + detail + secondary composition within the (16:9-capped) page width
  const pageWidth = Math.min(window.innerWidth, window.innerHeight * maxAspect)
  const compositionW = imageSize + detailW + secW
  const maxCompositionW = pageWidth * 0.95
  if (compositionW > maxCompositionW) {
    const shrink = maxCompositionW / compositionW
    imageSize *= shrink
    detailW *= shrink
    secW *= shrink
  }
  const detailH = imageSize * detailHeightFactor
  const overlap = imageSize * revealOverlapFactor
  const secondaries = pair.images.slice(1)

  const centreY = window.innerHeight / 2
  // detail card is the anchor: centred vertically, slightly right of page centre
  const detailX = window.innerWidth * (0.5 + detailCentreBias) - detailW / 2
  const detailTop = centreY - detailH / 2
  // primary image overlaps the detail on the left, near its top
  const imageX = detailX - imageSize + overlap
  const imageTop = detailTop + imageSize * 0.15
  // secondary images overlap the detail on the right, top-aligned with the primary image
  const secX = detailX + detailW - overlap
  const secStep = secW * 0.7 // stack the secondaries with a 30% overlap
  const columnTop = imageTop

  const imageRest = `translate(${imageX}px, ${imageTop}px) scale(1)`
  const detailRest = `translate(${detailX}px, ${detailTop}px) scale(1)`

  imageBoxRef.value = { w: imageSize, h: imageSize }
  detailBoxRef.value = { w: detailW, h: detailH }
  revealImageSizeRef.value = imageSize
  imageRestRef.value = imageRest
  focusKeyRef.value = null
  revealPairRef.value = pair
  revealImageRef.value = pair.images[0]
  setRevealFocal(pair.images[0])
  revealSecondaryRef.value = secondaries.map((image, i) => {
    const rest = `translate(${secX}px, ${columnTop + i * secStep}px) scale(1)`
    return { image, w: secW, dw: secW, dh: secW, restTransform: rest, transform: rest }
  })
  revealVisibleRef.value = false
  return { imageRest, detailRest, imageSize, detailW }
}

// the matched pair lifts out at full size (crisp): image card + detail card float from the grid slots
function startCelebrate(imageCard: PlayCard, infoCard: PlayCard) {
  const imageRect = cardEl(imageCard.key)?.getBoundingClientRect()
  const infoRect = cardEl(infoCard.key)?.getBoundingClientRect()
  if (!imageRect || !infoRect) {
    return
  }
  const { imageRest, detailRest, imageSize, detailW } = composeReveal(imageCard.pair)
  reviewModeRef.value = false
  imageTransformRef.value = `translate(${imageRect.left}px, ${imageRect.top}px) scale(${imageRect.width / imageSize})`
  detailTransformRef.value = `translate(${infoRect.left}px, ${infoRect.top}px) scale(${infoRect.width / detailW})`

  collectedKeysRef.value = [...collectedKeysRef.value, imageCard.key, infoCard.key]
  celebratingKeysRef.value = [imageCard.key, infoCard.key]
  phaseRef.value = 'celebrate'
  lockRef.value = true

  // start at the grid slots, then animate to the composition
  requestAnimationFrame(() => requestAnimationFrame(() => {
    imageTransformRef.value = imageRest
    detailTransformRef.value = detailRest
    revealVisibleRef.value = true
  }))
}

// tapping a collected pair in a team's stack re-opens its reveal (read-only, no scoring)
function reviewMatch(pair: MatchingCardPair, event: MouseEvent) {
  if (phaseRef.value !== 'idle') {
    return
  }
  reviewOriginEl = event.currentTarget as HTMLElement
  const originRect = reviewOriginEl.getBoundingClientRect()
  const { imageRest, detailRest, imageSize } = composeReveal(pair)
  reviewModeRef.value = true
  // the image flies up from the stack thumbnail; the detail + secondaries fade in
  imageTransformRef.value = `translate(${originRect.left}px, ${originRect.top}px) scale(${originRect.width / imageSize})`
  detailTransformRef.value = detailRest
  phaseRef.value = 'celebrate'

  requestAnimationFrame(() => requestAnimationFrame(() => {
    imageTransformRef.value = imageRest
    revealVisibleRef.value = true
  }))
}

// close a review: fly the image back to its stack thumbnail, fade the rest, then clear
function closeReview() {
  focusKeyRef.value = null
  revealVisibleRef.value = false
  const size = revealImageSizeRef.value
  imageBoxRef.value = { w: size, h: size }
  const originRect = reviewOriginEl?.getBoundingClientRect()
  if (originRect && size) {
    imageTransformRef.value = `translate(${originRect.left}px, ${originRect.top}px) scale(${originRect.width / size})`
  }
  window.setTimeout(() => {
    reviewModeRef.value = false
    phaseRef.value = 'idle'
    clearReveal()
  }, floatAnimMs)
}

// tapping off the reveal cards: collect the fresh match, or just close a review
function dismissReveal() {
  if (reviewModeRef.value) {
    closeReview()
  } else {
    collectMatch()
  }
}

// click an image to bring it to the centre showing the whole photo (natural aspect); the rest fades out
// Seed the primary reveal focal to the image's stored framing (the un-focused state).
function setRevealFocal(image?: MatchingGameImage) {
  cancelAnimationFrame(revealZoomRaf)
  revealFocalRef.value = image
    ? { focusX: image.focusX, focusY: image.focusY, zoom: image.zoom }
    : DEFAULT_FOCAL
}

// Ease the primary reveal focal's zoom to a target over the box-grow duration, keeping the focus
// point fixed. At zoom 1 in the (natural-aspect) focused box the whole image shows regardless of
// the focus point, so easing only zoom un-crops smoothly without the centre-snap.
function tweenRevealZoom(targetZoom: number) {
  cancelAnimationFrame(revealZoomRaf)
  const { focusX, focusY, zoom: startZoom } = revealFocalRef.value
  const startTime = performance.now()
  const duration = 600 // matches the .reveal-image transform/size transition
  const step = (now: number) => {
    const t = Math.min(1, (now - startTime) / duration)
    const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
    revealFocalRef.value = { focusX, focusY, zoom: startZoom + (targetZoom - startZoom) * eased }
    if (t < 1) {
      revealZoomRaf = requestAnimationFrame(step)
    }
  }
  revealZoomRaf = requestAnimationFrame(step)
}

function focusImage(key: string) {
  if (phaseRef.value !== 'celebrate') {
    return
  }
  if (focusKeyRef.value === key) {
    unfocusImage()
    return
  }
  focusKeyRef.value = key
  const box = focusBox(revealImgEl(key))
  const target = centredTransform(box)
  if (key === 'primary') {
    imageBoxRef.value = box
    imageTransformRef.value = target
    tweenRevealZoom(1)
  } else {
    const index = Number(key.slice(4))
    revealSecondaryRef.value = revealSecondaryRef.value.map((s, i) =>
      (i === index ? { ...s, dw: box.w, dh: box.h, transform: target } : s))
  }
}

function unfocusImage() {
  const key = focusKeyRef.value
  focusKeyRef.value = null
  if (key === 'primary') {
    const size = revealImageSizeRef.value
    imageBoxRef.value = { w: size, h: size }
    imageTransformRef.value = imageRestRef.value
    tweenRevealZoom(revealImageRef.value?.zoom ?? 1)
  } else if (key) {
    const index = Number(key.slice(4))
    revealSecondaryRef.value = revealSecondaryRef.value.map((s, i) =>
      (i === index ? { ...s, dw: s.w, dh: s.w, transform: s.restTransform } : s))
  }
}

// a box at 90% page height in the image's own aspect ratio, clamped to the page width
function focusBox(img: HTMLImageElement | null) {
  const ratio = img && img.naturalWidth ? img.naturalWidth / img.naturalHeight : 1
  let h = window.innerHeight * 0.9
  let w = h * ratio
  const maxW = window.innerWidth * 0.92
  if (w > maxW) {
    w = maxW
    h = w / ratio
  }
  return { w, h }
}

function centredTransform(box: { w: number; h: number }) {
  return `translate(${window.innerWidth / 2 - box.w / 2}px, ${window.innerHeight / 2 - box.h / 2}px) scale(1)`
}

function revealImgEl(key: string) {
  if (key === 'primary') {
    return document.querySelector<HTMLImageElement>('.reveal-image img')
  }
  const index = Number(key.slice(4))
  return document.querySelectorAll('.reveal-secondary')[index]?.querySelector('img') ?? null
}

// second click: the detail card and secondary images fade out, only the primary image flies to the team's stack
function collectMatch() {
  if (phaseRef.value !== 'celebrate') {
    return
  }
  phaseRef.value = 'collecting'
  focusKeyRef.value = null
  const imageSize = revealImageSizeRef.value
  // a focused image may have grown to its natural ratio: restore the square box for the fly-to-stack
  imageBoxRef.value = { w: imageSize, h: imageSize }
  revealSecondaryRef.value = revealSecondaryRef.value.map(s => ({ ...s, dw: s.w, dh: s.w, transform: s.restTransform }))
  revealVisibleRef.value = false
  const teamId = currentTeamRef.value
  const slot = nextStackSlot(teamId)
  if (slot && imageSize) {
    const scale = (stackItemSize * uiScaleRef.value) / imageSize
    const half = (imageSize * scale) / 2
    imageTransformRef.value = `translate(${slot.x - half}px, ${slot.y - half}px) scale(${scale})`
  }
  const pair = revealPairRef.value
  setTimeout(() => finishCollect(teamId, pair), floatAnimMs)
}

function nextStackSlot(teamId: number) {
  const stack = stackEls[teamId]
  if (!stack) {
    return null
  }
  const rect = stack.getBoundingClientRect()
  const advance = 59 * uiScaleRef.value // visible height of each overlapped stack card
  const count = teamMatchesRef.value[teamId].length
  return { x: rect.left + rect.width / 2, y: rect.top + advance * count + 42 * uiScaleRef.value }
}

function finishCollect(teamId: number, pair?: MatchingCardPair) {
  if (pair) {
    sounds.matchCollect()
    teamMatchesRef.value = teamMatchesRef.value.map((matches, id) =>
      id === teamId ? [...matches, pair] : matches)
    bumpScore(teamId)
  }
  clearReveal()
  flippedKeysRef.value = []
  phaseRef.value = 'idle'
  lockRef.value = false
  // a correct guess keeps the same team's turn
  if (wonRef.value) {
    setTimeout(() => { endGameRef.value = true }, 500)
  }
}

// restart the score bump animation for a team (remove then re-add so it re-triggers on repeat scores)
function bumpScore(teamId: number) {
  scoreBumpTeamsRef.value = scoreBumpTeamsRef.value.filter(id => id !== teamId)
  requestAnimationFrame(() => {
    scoreBumpTeamsRef.value = [...scoreBumpTeamsRef.value, teamId]
    window.setTimeout(() => {
      scoreBumpTeamsRef.value = scoreBumpTeamsRef.value.filter(id => id !== teamId)
    }, 800)
  })
}

function clearReveal() {
  revealPairRef.value = undefined
  revealImageRef.value = undefined
  revealSecondaryRef.value = []
  celebratingKeysRef.value = []
  revealVisibleRef.value = false
}

function setStackRef(teamId: number, el: unknown) {
  stackEls[teamId] = (el as HTMLElement) ?? null
}

function measureAlignment() {
  // base scale on capped viewport width, not card size, to avoid a panel<->board feedback loop.
  // done before the board/play guard so setup screens also scale in fullscreen.
  const cappedWidth = Math.min(window.innerWidth, window.innerHeight * maxAspect)
  uiScaleRef.value = Math.max(1, cappedWidth / uiScaleReferenceWidth)
  rootRef.value?.style.setProperty('--ui-scale', uiScaleRef.value.toFixed(3))

  const board = boardRef.value
  const play = playAreaRef.value
  if (!board || !play) {
    return
  }
  const playTop = play.getBoundingClientRect().top
  const colLabel = board.querySelector<HTMLElement>('.col-label')
  if (colLabel) {
    // align the team circles with the top of the column labels
    alignTopRef.value = Math.max(0, colLabel.getBoundingClientRect().top - playTop - circleInset)
  }
  const firstCard = board.querySelector<HTMLElement>('.card')
  if (firstCard && firstCard.offsetWidth > 0) {
    // Size the board to fit BOTH the horizontal space (between the team panels) and the height below
    // the play area, so near-square boards don't run off the bottom. Both budgets come from the
    // containers -- not the board's own width -- so capping the board can't feed back into the
    // measurement (offsetWidth here is only a visibility guard, skipped when hidden at end-game).
    const rows = rowsRef.value.length
    const cols = columnCountRef.value
    const areaWidth = (board.parentElement as HTMLElement).getBoundingClientRect().width
    const colLabelH = colLabel?.getBoundingClientRect().height ?? 0
    const rowLabelW = board.querySelector<HTMLElement>('.row-label')?.getBoundingClientRect().width ?? 0
    // each track carries a card plus a ~9% gap, so divide each budget by (count * 1.09)
    const cardByWidth = (areaWidth - rowLabelW) / (cols * 1.09)
    const cardByHeight = (window.innerHeight - playTop - 16 - colLabelH) / (rows * 1.09)
    const cardSize = Math.max(0, Math.min(cardByWidth, cardByHeight))
    gridCardSizeRef.value = cardSize
    gridGapRef.value = Math.round(cardSize * 0.09)
    boardMaxWidthRef.value = Math.floor(rowLabelW + cols * cardSize + cols * gridGapRef.value)
  }
  const indicators = [...play.querySelectorAll<HTMLElement>('.team-indicator')]
  if (indicators.length === 2) {
    teamCentersRef.value = indicators.map(el => {
      const rect = el.getBoundingClientRect()
      return rect.left + rect.width / 2
    })
    turnBarTopRef.value = indicators[0].getBoundingClientRect().top - 18 * uiScaleRef.value
  }
}

// on game start, the turn bar chooses who goes first: appear in the middle, pause, do a few
// short headfakes near the centre, then sweep continuously across to the first team
function spinTurnBar() {
  const centers = teamCentersRef.value
  if (centers.length < 2) {
    return
  }
  const middle = (centers[0] + centers[1]) / 2
  const fakeDist = Math.min(100, (centers[1] - centers[0]) * 0.14) // short nudge, not across the page
  const fakes = 2 + Math.floor(Math.random() * 3) // 2..4 headfakes
  const finalTeam = Math.random() < 0.5 ? 0 : 1
  const targetDir = finalTeam === 0 ? -1 : 1 // direction from the middle toward the first team
  const fakeMs = 260
  const settleMs = 600
  const pauseMs = 550

  // lock the board while the bar decides who goes first
  lockRef.value = true

  // appear in the middle, no motion
  turnBarTransitionRef.value = 'none'
  turnBarOverrideRef.value = middle
  turnBarVisibleRef.value = true

  // short headfakes oscillating around the middle; the last one lands on the side away from the
  // target so the final move is one continuous sweep across, never a stop-then-go same direction
  let t = pauseMs
  for (let i = 0; i < fakes; i++) {
    const dir = -targetDir * ((fakes - 1 - i) % 2 === 0 ? 1 : -1)
    window.setTimeout(() => {
      turnBarTransitionRef.value = `transform ${fakeMs}ms ease-in-out`
      turnBarOverrideRef.value = middle + dir * fakeDist
    }, t)
    t += fakeMs
  }

  // one decisive glide across to the first team
  window.setTimeout(() => {
    turnBarTransitionRef.value = `transform ${settleMs}ms ease-out`
    turnBarOverrideRef.value = centers[finalTeam]
  }, t)
  t += settleMs

  // lock in the first team, restore the slow transition, and hand control to the players
  window.setTimeout(() => {
    turnBarTransitionRef.value = 'transform 0.9s ease-in-out'
    currentTeamRef.value = finalTeam
    turnBarOverrideRef.value = null
    sounds.turnLanded(finalTeam)
    lockRef.value = false
  }, t + 40)
}

function imageSrc(image?: MatchingGameImage) {
  return image ? gamesApi.imagePathUrl(gameId.value, image.path) : ''
}

// Warm the browser cache for every game image while the player is still in setup, so nothing has to
// stream in (and visibly "wipe") when shown. Primaries first -- those hit the board the instant the
// game starts -- then the reveal-only secondaries. Limited concurrency so we don't saturate the link
// or starve the images the board is actively loading. Cheap and idempotent: kept refs so we can drop
// in-flight loads on unmount.
const preloadImgs: HTMLImageElement[] = []
let preloadStarted = false

function preloadImages() {
  if (preloadStarted) {
    return
  }
  preloadStarted = true
  const pairs = cardPairsRef.value
  const primaries = pairs.map(p => p.images[0])
  const secondaries = pairs.flatMap(p => p.images.slice(1))
  const urls = [...primaries, ...secondaries].map(img => imageSrc(img)).filter(Boolean)

  let next = 0
  const loadNext = () => {
    if (next >= urls.length) {
      return
    }
    const img = new Image()
    preloadImgs.push(img)
    img.onload = img.onerror = loadNext // chain the next as each finishes
    img.src = urls[next++]
  }
  for (let c = 0; c < 4; c++) {
    loadNext()
  }
}

// relationship path from the root person: F (father) = blue square, M (mother) = pink square
function relationshipPath(pair: MatchingCardPair) {
  return [...(pair.relationship || '')].map(step => (step === 'F' ? 'f' : 'm'))
}

// the person's given name -- flashed on an image card when it flips up, before the relationship squares
function firstName(pair: MatchingCardPair) {
  return pair.name.trim().split(/\s+/)[0]
}

function setTeamColor(teamId: number, color: string) {
  teamColorsRef.value = teamColorsRef.value.map((c, id) => (id === teamId ? color : c))
}

function setTeamName(teamId: number, name: string) {
  teamNamesRef.value = teamNamesRef.value.map((n, id) => (id === teamId ? name : n))
}

function startGame() {
  sounds.startGame()
  setupDoneRef.value = true
  dealReadyRef.value = false // cards appear after a short pause so the jingle plays first
  lockRef.value = true // hold until spinTurnBar unlocks after the deal animation
  requestAnimationFrame(() => requestAnimationFrame(() => {
    measureAlignment()
    setTimeout(() => {
      dealReadyRef.value = true
      sounds.placeCards()
      requestAnimationFrame(measureAlignment) // remeasure now that cards are in the DOM
      setTimeout(spinTurnBar, dealWaitMs)
    }, 500)
  }))
}

function toggleFullscreen() {
  if (document.fullscreenElement) {
    document.exitFullscreen()
  } else {
    rootRef.value?.requestFullscreen()
  }
}

function onFullscreenChange() {
  isFullscreenRef.value = !!document.fullscreenElement
  requestAnimationFrame(measureAlignment)
}

onMounted(() => {
  document.addEventListener('fullscreenchange', onFullscreenChange)
  window.addEventListener('resize', measureAlignment)
  measureAlignment()
})
onUnmounted(() => {
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  window.removeEventListener('resize', measureAlignment)
  window.removeEventListener('click', onMismatchClick, true)
  window.clearTimeout(peekTimer)
  // stop preloading if the player leaves mid-load
  preloadImgs.forEach((img) => { img.onload = img.onerror = null; img.src = '' })
})
</script>
